import type { RecordingDecryptor } from './client.js';

/**
 * The sealed-recording format, in one place so a reader is never guessing at offsets.
 *
 * `"FIG1" | nonce(12) | ciphertext | tag(16)` — written by
 * `KeyVaultRecordingCipher.SealAsync` and verified against the real Key Vault on 2026-09-26.
 *
 * Two details are load-bearing. The **magic prefix** means ciphertext is distinguishable from an
 * MP3 by looking at the bytes rather than by trusting a database column, which is what makes the
 * mixed state during backfill safe. And **the tag trails the ciphertext**, which is exactly what
 * WebCrypto's AES-GCM expects — so the browser needs no library at all, and neither does a
 * hand-written page like the shared-call view.
 */
export const SEALED_MAGIC = 'FIG1';
export const SEALED_NONCE_BYTES = 12;
export const SEALED_TAG_BYTES = 16;
export const SEALED_HEADER_BYTES = 4 + SEALED_NONCE_BYTES;
export const SEALED_CIPHER = 'AES-256-GCM/FIG1';

/** Thrown when the bytes are not a sealed Fig recording at all. */
export class MalformedSealedRecordingError extends Error {
  constructor(reason: string) {
    super(`This recording could not be read: ${reason}`);
    this.name = 'MalformedSealedRecordingError';
  }
}

function base64ToBytes(b64: string): Uint8Array {
  // atob exists in browsers and in React Native's Hermes; Buffer does not exist in either.
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Copies a view into a standalone ArrayBuffer.
 *
 * **Why a copy rather than `view.buffer`.** A `Uint8Array`'s `buffer` is typed `ArrayBufferLike`,
 * which TypeScript will not accept as WebCrypto's `BufferSource` under the stricter generic
 * `Uint8Array<ArrayBuffer>` that React Native's config turns on — and it would be genuinely wrong
 * anyway, because `subarray` returns a view over the WHOLE original buffer, so passing `.buffer`
 * would hand WebCrypto far more bytes than intended. The copy is correct and version-portable, and
 * it is a few hundred kilobytes once per playback.
 */
function toArrayBuffer(view: Uint8Array): ArrayBuffer {
  const out = new ArrayBuffer(view.byteLength);
  new Uint8Array(out).set(view);
  return out;
}

/**
 * Splits a sealed blob into the nonce and the AES-GCM body (ciphertext with its tag attached).
 *
 * Exported because every decryptor needs it and the offsets must not be written twice.
 */
export function parseSealedRecording(sealed: ArrayBuffer): {
  nonce: Uint8Array; body: Uint8Array;
} {
  const bytes = new Uint8Array(sealed);
  if (bytes.byteLength < SEALED_HEADER_BYTES + SEALED_TAG_BYTES) {
    throw new MalformedSealedRecordingError('it is too short to be encrypted audio');
  }
  const magic = String.fromCharCode(...bytes.subarray(0, 4));
  if (magic !== SEALED_MAGIC) {
    // The likeliest cause is a plaintext MP3 arriving on the sealed path, which would otherwise
    // fail as an authentication error and send somebody looking at keys.
    throw new MalformedSealedRecordingError(
      `it does not start with ${SEALED_MAGIC} (found "${magic}") — these bytes are not encrypted`,
    );
  }
  return {
    nonce: bytes.subarray(4, SEALED_HEADER_BYTES),
    body: bytes.subarray(SEALED_HEADER_BYTES),
  };
}

/**
 * A {@link RecordingDecryptor} built on WebCrypto. **No dependency**, which is why it can live in
 * the core: `crypto.subtle` is native in every browser, and in Node 20+ for tests.
 *
 * Use this in `fig-client-portal` and in the shared-call page. React Native's Hermes has no
 * `crypto.subtle`, so `fig-mobile` supplies its own — see that repo's `src/audio/decrypt.ts`.
 *
 * <b>It throws on a bad authentication tag, and that is the point.</b> A failure here means the
 * bytes are not what the key says they are, which is a real signal and must never be softened into
 * playing something.
 */
export const webCryptoDecryptor: RecordingDecryptor = async (sealed, keyBase64, cipher) => {
  if (cipher !== SEALED_CIPHER) {
    throw new MalformedSealedRecordingError(`unknown format "${cipher}"`);
  }
  const { nonce, body } = parseSealedRecording(sealed);

  const key = await crypto.subtle.importKey(
    'raw', toArrayBuffer(base64ToBytes(keyBase64)), { name: 'AES-GCM' }, false, ['decrypt'],
  );

  // tagLength is in BITS, and the default is already 128 — stated explicitly because getting it
  // wrong fails as an authentication error rather than as a configuration one.
  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(nonce), tagLength: SEALED_TAG_BYTES * 8 },
    key, toArrayBuffer(body),
  );
};
